"use client";

import {
  type CodeHeaderProps,
  type SyntaxHighlighterProps,
} from "@assistant-ui/react-markdown";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import {
  CheckIcon,
  CodeIcon,
  CopyIcon,
  EyeIcon,
  MaximizeIcon,
  MinusIcon,
  PlusIcon,
  RotateCcwIcon,
  XIcon,
} from "lucide-react";

import * as DialogPrimitive from "@radix-ui/react-dialog";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";

// Dynamic import — created once, reused across all instances.
// Guarded for SSR safety.
const mermaidPromise =
  typeof window !== "undefined"
    ? import("mermaid").then((m) => {
        m.default.initialize({
          startOnLoad: false,
          theme: "default",
          securityLevel: "strict",
        });
        return m.default;
      })
    : null;

// ---------------------------------------------------------------------------
// Serialized render queue — ensures only one mermaid.render() runs at a time
// and yields to the browser between renders so the UI stays responsive.
// ---------------------------------------------------------------------------
type QueueEntry = {
  id: string;
  code: string;
  resolve: (svg: string) => void;
  reject: (err: unknown) => void;
};

const renderQueue: QueueEntry[] = [];
let isProcessing = false;

/** Yield to the browser so it can paint / handle input between renders. */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => setTimeout(resolve, 0)),
  );
}

async function processQueue() {
  if (isProcessing) return;
  isProcessing = true;

  while (renderQueue.length > 0) {
    const entry = renderQueue.shift()!;
    try {
      const mermaid = await mermaidPromise!;
      const { svg } = await mermaid.render(entry.id, entry.code);
      entry.resolve(svg);
    } catch (e) {
      entry.reject(e);
    }
    // Yield between renders so the browser can paint and handle events.
    if (renderQueue.length > 0) await yieldToMain();
  }

  isProcessing = false;
}

function enqueueRender(id: string, code: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    renderQueue.push({ id, code, resolve, reject });
    processQueue();
  });
}

function dequeueRender(id: string) {
  const idx = renderQueue.findIndex((e) => e.id === id);
  if (idx !== -1) renderQueue.splice(idx, 1);
}

// ---------------------------------------------------------------------------
// SVG dimension helper — used by the lightbox to calculate fit-to-view scale.
// ---------------------------------------------------------------------------
function getSvgDimensions(svgHtml: string): {
  width: number;
  height: number;
} | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgHtml, "image/svg+xml");
  const svg = doc.querySelector("svg");
  if (!svg) return null;

  let w = parseFloat(svg.getAttribute("width") ?? "0");
  let h = parseFloat(svg.getAttribute("height") ?? "0");

  if (!w || !h) {
    const vb = svg.getAttribute("viewBox");
    if (vb) {
      const parts = vb.split(/[\s,]+/).map(Number);
      if (parts.length === 4) {
        w = parts[2];
        h = parts[3];
      }
    }
  }

  return w && h ? { width: w, height: h } : null;
}

// ---------------------------------------------------------------------------
// MermaidLightbox — fullscreen dialog with pan/zoom
// ---------------------------------------------------------------------------
const MIN_SCALE = 0.1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;

function MermaidLightbox({
  svgHtml,
  open,
  onOpenChange,
}: {
  svgHtml: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ active: false, pointerId: -1, x: 0, y: 0, tx: 0, ty: 0 });

  // Reset view when the lightbox opens — use ResizeObserver to wait
  // until the container actually has layout dimensions.
  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width: cw, height: ch } = entry.contentRect;
      if (cw === 0 || ch === 0) return; // not laid out yet
      observer.disconnect();

      const dims = getSvgDimensions(svgHtml);
      if (!dims) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
        return;
      }

      const fitScale = Math.min(cw / dims.width, ch / dims.height);
      setScale(fitScale);
      setTranslate({
        x: (cw - dims.width * fitScale) / 2,
        y: (ch - dims.height * fitScale) / 2,
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [open, svgHtml]);

  const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  const handleWheel = useCallback(
    (e: ReactWheelEvent) => {
      e.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Cursor position relative to container.
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      const direction = e.deltaY < 0 ? 1 : -1;
      const newScale = clampScale(scale + direction * ZOOM_STEP);
      const ratio = newScale / scale;

      // Zoom toward cursor position.
      setTranslate({
        x: cx - ratio * (cx - translate.x),
        y: cy - ratio * (cy - translate.y),
      });
      setScale(newScale);
    },
    [scale, translate],
  );

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button !== 0) return;
      const container = containerRef.current;
      if (!container) return;

      // Capture on the container — NOT e.target (which may be a moving SVG child).
      container.setPointerCapture(e.pointerId);
      dragRef.current = {
        active: true,
        pointerId: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        tx: translate.x,
        ty: translate.y,
      };
      setIsDragging(true);
    },
    [translate],
  );

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!dragRef.current.active) return;
      setTranslate({
        x: dragRef.current.tx + (e.clientX - dragRef.current.x),
        y: dragRef.current.ty + (e.clientY - dragRef.current.y),
      });
    },
    [],
  );

  const handlePointerUp = useCallback(() => {
    dragRef.current.active = false;
    setIsDragging(false);
  }, []);

  const resetView = useCallback(() => {
    const dims = getSvgDimensions(svgHtml);
    const container = containerRef.current;
    if (!dims || !container) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      return;
    }
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const fitScale = Math.min(cw / dims.width, ch / dims.height);
    setScale(fitScale);
    setTranslate({
      x: (cw - dims.width * fitScale) / 2,
      y: (ch - dims.height * fitScale) / 2,
    });
  }, [svgHtml]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border bg-background shadow-2xl">
            <DialogTitle className="sr-only">
              {t("markdown.diagramPreview")}
            </DialogTitle>

            {/* Toolbar */}
            <div className="flex shrink-0 items-center justify-between border-b bg-muted/40 px-3 py-2">
              <div className="flex items-center gap-1">
                <TooltipIconButton
                  tooltip={t("markdown.zoomIn")}
                  onClick={() => setScale((s) => clampScale(s + ZOOM_STEP))}
                >
                  <PlusIcon />
                </TooltipIconButton>
                <TooltipIconButton
                  tooltip={t("markdown.zoomOut")}
                  onClick={() => setScale((s) => clampScale(s - ZOOM_STEP))}
                >
                  <MinusIcon />
                </TooltipIconButton>
                <TooltipIconButton
                  tooltip={t("markdown.resetZoom")}
                  onClick={resetView}
                >
                  <RotateCcwIcon />
                </TooltipIconButton>
                <span className="ml-2 select-none text-muted-foreground text-xs">
                  {Math.round(scale * 100)}%
                </span>
              </div>
              <TooltipIconButton
                tooltip={t("common.close")}
                onClick={() => onOpenChange(false)}
              >
                <XIcon />
              </TooltipIconButton>
            </div>

            {/* Pan / zoom viewport */}
            <div
              ref={containerRef}
              className="relative flex-1 select-none overflow-hidden"
              style={{ cursor: isDragging ? "grabbing" : "grab" }}
              onWheel={handleWheel}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onDoubleClick={resetView}
            >
              <div
                className="pointer-events-none origin-top-left [&>svg]:h-auto [&>svg]:max-w-none"
                style={{
                  transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                }}
                dangerouslySetInnerHTML={{ __html: svgHtml }}
              />
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// MermaidCodeHeader & MermaidDiagram (public exports)
// ---------------------------------------------------------------------------

/** No-op header — the header bar is rendered inside MermaidDiagram instead. */
export const MermaidCodeHeader: React.FC<CodeHeaderProps> = () => null;

export const MermaidDiagram: React.FC<SyntaxHighlighterProps> = ({
  components: { Pre, Code },
  code,
}) => {
  const { t } = useTranslation();
  const rawId = useId();
  const safeId = `mermaid-${rawId.replace(/:/g, "")}`;

  const [svgHtml, setSvgHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [showCode, setShowCode] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  // Lazy render: only trigger mermaid when the component scrolls into view.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Render the diagram once visible. Uses a short debounce so that rapid
  // code updates during streaming don't thrash the render queue — the
  // diagram re-renders each time the code stabilizes for 50ms.
  useEffect(() => {
    if (!isVisible || !mermaidPromise) return;

    setIsRendering(true);
    let cancelled = false;

    const timer = setTimeout(() => {
      enqueueRender(safeId, code)
        .then((svg) => {
          if (!cancelled) {
            setSvgHtml(svg);
            setError(null);
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setError(
              e instanceof Error ? e.message : "Failed to render diagram",
            );
            setSvgHtml(null);
          }
        })
        .finally(() => {
          if (!cancelled) setIsRendering(false);
        });
    }, 50);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      dequeueRender(safeId);
    };
  }, [code, isVisible, safeId]);

  const body = (() => {
    if (showCode) {
      return (
        <Pre>
          <Code>{code}</Code>
        </Pre>
      );
    }

    if (error) {
      return (
        <div className="rounded-b-lg border border-t-0 border-destructive/30 bg-destructive/5 p-4">
          <p className="mb-2 text-sm text-destructive">{error}</p>
          <Pre>
            <Code>{code}</Code>
          </Pre>
        </div>
      );
    }

    if (isRendering || !svgHtml) {
      return (
        <div className="flex items-center justify-center rounded-b-lg bg-background p-8">
          <div className="h-32 w-full animate-pulse rounded bg-muted" />
        </div>
      );
    }

    return (
      <div
        className="group relative cursor-pointer overflow-x-auto rounded-b-lg bg-background p-4 [&>div>svg]:h-auto [&>div>svg]:max-w-full"
        onClick={() => setLightboxOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setLightboxOpen(true);
          }
        }}
        aria-label={t("markdown.expandDiagram")}
      >
        <div dangerouslySetInnerHTML={{ __html: svgHtml }} />
        {/* Hover overlay hint */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-b-lg bg-black/0 transition-colors group-hover:bg-black/5 dark:group-hover:bg-white/5">
          <MaximizeIcon className="size-8 text-foreground/0 transition-colors group-hover:text-foreground/40" />
        </div>
      </div>
    );
  })();

  return (
    <div className="mt-4" ref={containerRef}>
      {/* Header bar — mirrors CodeHeader styling */}
      <div className="flex items-center justify-between gap-4 rounded-t-lg bg-muted-foreground/15 px-4 py-2 font-semibold text-foreground text-sm dark:bg-muted-foreground/20">
        <span className="lowercase">mermaid</span>
        <div className="flex items-center gap-0.5">
          <TooltipIconButton
            tooltip={showCode ? t("markdown.showDiagram") : t("markdown.showCode")}
            onClick={() => setShowCode((v) => !v)}
          >
            {showCode ? <EyeIcon /> : <CodeIcon />}
          </TooltipIconButton>
          <TooltipIconButton
            tooltip={t("markdown.copy")}
            onClick={() => !isCopied && copyToClipboard(code)}
          >
            {isCopied ? <CheckIcon /> : <CopyIcon />}
          </TooltipIconButton>
        </div>
      </div>

      {body}

      {svgHtml && (
        <MermaidLightbox
          svgHtml={svgHtml}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      )}
    </div>
  );
};
